import React from "react";

/**
 * Shared section shell — keeps a consistent book/letter rhythm across all blocks:
 * - centered eyebrow (gold uppercase)
 * - centered serif title (with optional gold-accent fragment)
 * - left-aligned, justified, hyphenated body prose
 * - optional subtle background tint for visual rhythm
 * - optional bottom hairline
 */
export default function SectionShell({
  id,
  testId,
  eyebrow,
  titleStart,
  titleAccent,
  titleEnd,
  titleBreak = false,
  children,
  tinted = false,
  tight = false,
  hairline = true,
  maxWidth = "max-w-5xl",
}) {
  return (
    <section
      id={id}
      data-testid={testId}
      className={`relative ${tight ? "py-7 md:py-10" : "py-10 md:py-14"} px-6 md:px-10 ${tinted ? "bg-tinted" : ""}`}
    >
      <div className={`${maxWidth} mx-auto`}>
        {eyebrow && (
          <p
            data-testid={`${testId}-eyebrow`}
            className="text-[12px] tracking-[0.35em] uppercase text-gold mb-5 font-body text-center"
          >
            — {eyebrow}
          </p>
        )}

        {(titleStart || titleAccent || titleEnd) && (
          <h2
            data-testid={`${testId}-title`}
            className={`font-serif-display text-[34px] sm:text-[40px] md:text-[46px] leading-[1.18] text-graphite ${tight ? "mb-7" : "mb-10"} text-center`}
          >
            {titleStart}
            {titleAccent && (
              <>
                {titleStart ? (titleBreak ? <br /> : " ") : ""}
                <em className="text-gold" style={{ fontStyle: "italic" }}>
                  {titleAccent}
                </em>
              </>
            )}
            {titleEnd}
          </h2>
        )}

        <div className="reading-prose text-[16px] md:text-[17px] leading-[1.9] text-graphite font-body font-light">
          {children}
        </div>
      </div>

      {hairline && <div className={`${maxWidth} mx-auto ${tight ? "mt-7 md:mt-8" : "mt-10 md:mt-12"} hairline`} />}
    </section>
  );
}

/* Helper centered highlight — italic serif, generous spacing, no hairlines */
export function CenteredHighlight({ children, testId, dataTestid }) {
  return (
    <div
      data-testid={testId || dataTestid}
      className="my-10 md:my-12 text-center"
    >
      <p className="font-serif-display italic text-[22px] sm:text-[24px] md:text-[27px] leading-[1.45] text-graphite max-w-xl mx-auto">
        {children}
      </p>
    </div>
  );
}
